import { IAirportData, IGeogridData, IProjectData, IDynamicProjectOutputData, IAASHTOResults, IPavedCalculatedDesignThickness, IPavedData, IUnpavedData, IRailwayData } from '../api/IApiResponse'; // Assicurati che IProjectData sia il tipo corretto per projectInfo.data
import { EProjectType, unit as unitEnum } from '../enum/project';
import { enhancePavedGeogridMappingWithCalculatedResults } from './PavedGeogridMappingEnhancer';
import { ProjectTypeJson } from './ValidationService';
import { IRailwayCalculationResult } from './RailwayCalculationService';
import { IRailwayDataWithGeogrid } from '../utils/railwayTypes';
import { mapGeogridTypeToDropdownValue } from '../constants/geogridTypeMapping';
import { GEOGRID_CONFIG } from '../config/GeogridConfig';
import { keepSavedGeogridSafetyFactor } from './GeogridSafetyFactor';

/**
 * @file GeoGridDataMapper.ts
 * @description Maps data from other parts of the project (e.g., BCR for container_yard)
 * to the GeoGrid data structure. This allows pre-populating GeoGrid fields based on
 * data entered in other modules of a specific project type.
 */

/**
 * Helper function to check if a field should be mapped or preserved
 * @param currentValue - The current value in the target object
 * @param preserveUserValues - Whether to preserve existing user values
 * @returns true if the field should be mapped, false if it should be preserved
 */
const shouldMapField = (currentValue: any, preserveUserValues: boolean): boolean => {
    if (!preserveUserValues) return true;
    return currentValue === undefined || currentValue === null;
};

type WheelLoadSource = { load?: { wheelLoad?: unknown } } | undefined;

/**
 * Parking Deck GeoGrid: the Circular calculation reads W (load.designAxleLoad), never load.wheelLoad, so a missing W
 * starts from the project's wheel load P as W = 2 × P. A W already present is kept whatever the run/modified flags say:
 * after a reload those flags are reset and cannot tell a user's W from a default one. Single authority for this rule,
 * used whether the saved geoGrid is preserved or remapped.
 * @returns the same geoGrid when nothing changes, otherwise a copy with W set
 */
export const fillParkingDeckDesignAxleLoad = (projectFullData: IProjectData, geoGridData: IGeogridData): IGeogridData => {
    if (projectFullData.type !== EProjectType.parking_deck || !shouldMapField(geoGridData.load?.designAxleLoad, true)) {
        return geoGridData;
    }
    // FLAT (Redux) keeps P in load.wheelLoad for both methods; legacy NESTED keeps it under static/bcr, static first
    const flatData = projectFullData as WheelLoadSource;
    const nestedData = projectFullData as { static?: WheelLoadSource; bcr?: WheelLoadSource };
    const projectWheelLoad = (nestedData.static ?? flatData)?.load?.wheelLoad ?? (nestedData.bcr ?? flatData)?.load?.wheelLoad;
    if (typeof projectWheelLoad !== 'number' || !Number.isFinite(projectWheelLoad)) {
        return geoGridData;
    }
    return { ...geoGridData, load: { ...geoGridData.load, designAxleLoad: 2 * projectWheelLoad } };
};

/**
 * Maps relevant project-specific data to the GeoGrid data structure.
 *
 * This function checks the project type and applies specific mapping rules.
 * For example, for 'container_yard' projects, it can map values from
 * the 'bcr.load' section to 'geoGrid.load'.
 *
 * It starts with a deep clone of any existing `projectFullData.geoGrid` data
 * or an empty object, then applies the defined mappings, potentially overriding
 * existing GeoGrid values if a mapping rule targets an already populated field.
 *
 * @param projectFullData - The complete `projectInfo.data` object which might contain
 *                          source data for mapping (e.g., `bcr`, `static`, etc.) and
 *                          any pre-existing `geoGrid` data.
 * @param outputData - The output data from calculations that might contain values to map.
 * @param preserveUserValues - If true, preserves existing geoGrid values that were modified by the user
 *                            instead of overriding them with mapped values. Default is false.
 * @param aashtoResults - Optional AASHTO calculation results for enhanced paved project mapping.
 * @param calculatedDesignThickness - Optional calculated thickness values from selected design for paved projects.
 * @param targetUnit - Target unit system for conversion (if conversion is needed)
 * @param configFields - Field configurations for unit conversion (if conversion is needed)
 * @returns Enhanced geoGrid data with mapped values
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * Maps data from existing projects (paved, railway, container_yard, airport) to geogrid form format.
 * Used when user navigates from an existing project to geogrid studio ("Show Geogrid" button).
 *
 * @param projectFullData - Complete project data from existing project
 * @param outputData - Optional output data from project calculations
 * @param aashtoResults - Optional AASHTO calculation results
 * @param calculatedDesignThickness - Optional calculated thickness data
 * @param targetUnit - Target unit system for conversion
 * @param configFields - Configuration fields for the project type
 * @param project - Project metadata with unit information
 * @param preserveUserValues - Whether to preserve existing user values
 * @returns Partial geogrid data ready for form initialization
 */
export const mapExistingProjectToGeogridForm = (
    projectFullData: IProjectData,
    outputData?: IDynamicProjectOutputData,
    aashtoResults?: IAASHTOResults,
    calculatedDesignThickness?: IPavedCalculatedDesignThickness,
    targetUnit?: unitEnum,
    configFields?: ProjectTypeJson['fields'],
    project?: {
        unit: unitEnum;
        conversionApplied?: boolean;
        originalUnit?: unitEnum;
    },
    preserveUserValues = false
): Partial<IGeogridData> => {
    if (!projectFullData) {
        return {};
    }

    // Start with a deep clone of existing geoGrid data or an empty object.
    // This prevents mutation of the original state object and provides a base.
    const targetGeoGridData: IGeogridData = projectFullData.geoGrid ? JSON.parse(JSON.stringify(projectFullData.geoGrid)) : {};

    // If preserveUserValues is true and we have existing geoGrid data, return it without mapping
    if (preserveUserValues && projectFullData.geoGrid) {
        return fillParkingDeckDesignAxleLoad(projectFullData, targetGeoGridData);
    }

    // --- Mapping for "container_yard" projects ---
    if (projectFullData.type === EProjectType.container_yard) {
        // Support both FLAT (PD3/PD4: fields at root) and NESTED (legacy: under bcr/static keys)
        const flatData = projectFullData as any;
        const bcrData = projectFullData.bcr ?? { load: flatData.load, embankmentCharacteristics: flatData.embankmentCharacteristics, subgradeCharacteristics: flatData.subgradeCharacteristics, subgrade: flatData.subgrade, fsDesign: flatData.fsDesign };
        const staticData = projectFullData.static ?? { load: flatData.load, subgrade: flatData.subgrade, fsDesign: flatData.fsDesign };
        if (bcrData?.load?.deckWidth !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map bcr.load.deckWidth to geoGrid.load.deckHalfWidth only if field should be mapped
            if (shouldMapField(targetGeoGridData.load.deckHalfWidth, preserveUserValues)) {
                targetGeoGridData.load.deckHalfWidth = bcrData.load.deckWidth / 2;
            }
        }
        // Add other 'container_yard' specific mappings here. For example:
        if (bcrData?.load?.deckLength !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map bcr.load.deckLength to geoGrid.load.deckHalfLength only if field should be mapped
            if (shouldMapField(targetGeoGridData.load.deckHalfLength, preserveUserValues)) {
                targetGeoGridData.load.deckHalfLength = bcrData.load.deckLength / 2;
            }
        }
        if (bcrData?.load?.uniformLoad !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map bcr.load.uniformLoad to geoGrid.load.uniformLoad only if field should be mapped
            if (shouldMapField(targetGeoGridData.load.uniformLoad, preserveUserValues)) {
                targetGeoGridData.load.uniformLoad = bcrData.load.uniformLoad;
            }
        }
        if (bcrData?.embankmentCharacteristics && bcrData.embankmentCharacteristics.unitWeight !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map bcr.embankmentCharacteristics.unitWeight to geoGrid.baseCourse.unitWeight only if field should be mapped
            if (shouldMapField(targetGeoGridData.baseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.baseCourse.unitWeight = bcrData.embankmentCharacteristics.unitWeight;
            }
            // Map bcr.embankmentCharacteristics.unitWeight to geoGrid.subBaseCourse.unitWeight
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.unitWeight = bcrData.embankmentCharacteristics.unitWeight;
            }
        }
        if (bcrData?.embankmentCharacteristics && bcrData.embankmentCharacteristics.frictionAngle !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map bcr.embankmentCharacteristics.frictionAngle to geoGrid.baseCourse.frictionAngle only if field should be mapped
            if (shouldMapField(targetGeoGridData.baseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.frictionAngle = bcrData.embankmentCharacteristics.frictionAngle;
            }
        }
        if (bcrData?.embankmentCharacteristics && bcrData.embankmentCharacteristics.cohesion !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map bcr.embankmentCharacteristics.cohesion to geoGrid.baseCourse.cohesion only if field should be mapped
            if (shouldMapField(targetGeoGridData.baseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.baseCourse.cohesion = bcrData.embankmentCharacteristics.cohesion;
            }
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.cohesion = bcrData.embankmentCharacteristics.cohesion;
            }
        }
        if (bcrData?.fsDesign && bcrData.fsDesign.fsDesign !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subgrade.safetyFactor, preserveUserValues)) {
                targetGeoGridData.subgrade.safetyFactor = bcrData.fsDesign.fsDesign;
            }
        }
        if (staticData?.load?.deckHalfWidth !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map static.load.deckHalfWidth to geoGrid.load.deckHalfWidth only if field should be mapped
            if (shouldMapField(targetGeoGridData.load.deckHalfWidth, preserveUserValues)) {
                targetGeoGridData.load.deckHalfWidth = staticData.load.deckHalfWidth;
            }
        }
        if (staticData?.load?.deckHalfLength !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map static.load.deckHalfLength to geoGrid.load.deckHalfLength only if field should be mapped
            if (shouldMapField(targetGeoGridData.load.deckHalfLength, preserveUserValues)) {
                targetGeoGridData.load.deckHalfLength = staticData.load.deckHalfLength;
            }
        }
        if (staticData?.load?.uniformLoad !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map static.load.uniformLoad to geoGrid.load.uniformLoad
            targetGeoGridData.load.uniformLoad = staticData.load.uniformLoad;
        }
        if (staticData?.subgrade?.cbrSubgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            // Map static.subgrade.cbrSubgrade to geoGrid.subgrade.cbr
            targetGeoGridData.subgrade.cbrSubgrade = staticData.subgrade.cbrSubgrade;
        }
        if (staticData?.subgrade?.deformationModulus !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            // Map static.subgrade.deformationModulus to geoGrid.subgrade.deformationModulus
            targetGeoGridData.subgrade.deformationModulus = staticData.subgrade.deformationModulus;
        }
        if (staticData?.fsDesign?.fsDesign !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            // Map static.fsDesign.fsDesign to geoGrid.subgrade.safetyFactor
            targetGeoGridData.subgrade.safetyFactor = staticData.fsDesign.fsDesign;
        }
        //ora dobbiamo vedere se nei results del bcr c'è reinforced deck e metterlo nella baseCourse thickness
        if (outputData?.deckDesign?.reinforcedHeight !== undefined) {
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            targetGeoGridData.geogridBaseCourse.thickness = outputData.deckDesign.reinforcedHeight;
        }
    }
    // --- End of "container_yard" mapping ---
    else if (projectFullData.type === EProjectType.parking_deck) {
        // Support both FLAT (PD3/PD4: fields at root) and NESTED (legacy: under bcr/static keys)
        const flatData = projectFullData as any;
        const bcrData = projectFullData.bcr ?? { load: flatData.load, embankmentCharacteristics: flatData.embankmentCharacteristics, subgradeCharacteristics: flatData.subgradeCharacteristics, subgrade: flatData.subgrade, fsDesign: flatData.fsDesign };
        const staticData = projectFullData.static ?? { load: flatData.load, subgrade: flatData.subgrade, fsDesign: flatData.fsDesign };
        if (bcrData?.load?.wheelLoad !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map bcr.load.wheelLoad to geoGrid.load.wheelLoad
            targetGeoGridData.load.wheelLoad = bcrData.load.wheelLoad;
        }
        if (bcrData?.load?.tireInflationPressure !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map bcr.load.tireInflationPressure to geoGrid.load.tireInflationPressure
            targetGeoGridData.load.tireInflationPressure = bcrData.load.tireInflationPressure;
        }
        if (bcrData?.embankmentCharacteristics && bcrData.embankmentCharacteristics.unitWeight !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map bcr.embankmentCharacteristics.unitWeight to geoGrid.baseCourse.unitWeight
            targetGeoGridData.baseCourse.unitWeight = bcrData.embankmentCharacteristics.unitWeight;
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            targetGeoGridData.subBaseCourse.unitWeight = bcrData.embankmentCharacteristics.unitWeight;
        }
        if (bcrData?.embankmentCharacteristics && bcrData.embankmentCharacteristics.frictionAngle !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map bcr.embankmentCharacteristics.frictionAngle to geoGrid.baseCourse.frictionAngle
            targetGeoGridData.baseCourse.frictionAngle = bcrData.embankmentCharacteristics.frictionAngle;
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            targetGeoGridData.subBaseCourse.frictionAngle = bcrData.embankmentCharacteristics.frictionAngle;
        }
        if (bcrData?.embankmentCharacteristics && bcrData.embankmentCharacteristics.cohesion !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map bcr.embankmentCharacteristics.cohesion to geoGrid.baseCourse.cohesion
            targetGeoGridData.baseCourse.cohesion = bcrData.embankmentCharacteristics.cohesion;
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            targetGeoGridData.subBaseCourse.cohesion = bcrData.embankmentCharacteristics.cohesion;
        }
        if (bcrData?.fsDesign && bcrData.fsDesign.fsDesign !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            targetGeoGridData.subgrade.safetyFactor = bcrData.fsDesign.fsDesign;
        }
        if (staticData?.load?.wheelLoad !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map static.load.wheelLoad to geoGrid.load.wheelLoad
            targetGeoGridData.load.wheelLoad = staticData.load.wheelLoad;
        }
        if (staticData?.load?.tireInflationPressure !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map static.load.tireInflationPressure to geoGrid.load.tireInflationPressure
            targetGeoGridData.load.tireInflationPressure = staticData.load.tireInflationPressure;
        }
        if (staticData?.subgrade?.cbrSubgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            // Map static.subgrade.cbrSubgrade to geoGrid.subgrade.cbr
            targetGeoGridData.subgrade.cbrSubgrade = staticData.subgrade.cbrSubgrade;
        }
        if (staticData?.subgrade?.deformationModulus !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            // Map static.subgrade.deformationModulus to geoGrid.subgrade.deformationModulus
            targetGeoGridData.subgrade.deformationModulus = staticData.subgrade.deformationModulus;
        }
        if (staticData?.fsDesign?.fsDesign !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            // Map static.fsDesign.fsDesign to geoGrid.subgrade.safetyFactor
            targetGeoGridData.subgrade.safetyFactor = staticData.fsDesign.fsDesign;
        }
        if (outputData?.deckDesign?.reinforcedHeight !== undefined) {
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            targetGeoGridData.geogridBaseCourse.thickness = outputData.deckDesign.reinforcedHeight;
        }
        Object.assign(targetGeoGridData, fillParkingDeckDesignAxleLoad(projectFullData, targetGeoGridData));
    }
    // --- End of "parking_deck" mapping ---
    else if (projectFullData.type === EProjectType.paved) {
        // Frontend standard is FLAT (fields at root). Keep asphalt fallback only for legacy payloads.
        const pavedData = projectFullData as IPavedData;
        const pavedTrafficLoad = pavedData.trafficLoad ?? pavedData.asphalt?.trafficLoad;
        const pavedSurface = pavedData.surface ?? pavedData.asphalt?.surface;
        const pavedBaseCourse = pavedData.baseCourse ?? pavedData.asphalt?.baseCourse;
        const pavedSubbaseCourse = pavedData.subbaseCourse ?? pavedData.asphalt?.subbaseCourse;
        const pavedSubgrade = pavedData.subgrade ?? pavedData.asphalt?.subgrade;

        // Initialize type object for paved projects to avoid warning in ensureGeoGridType
        if (!targetGeoGridData.type) {
            targetGeoGridData.type = { geogridType: 'Circular' };
        }

        // Map wheel load information from traffic load section
        if (pavedTrafficLoad?.esalFinalValue !== undefined) {
            if (!targetGeoGridData.truck) {
                targetGeoGridData.truck = {}; // Initialize geoGrid.truck if it doesn't exist
            }
            // Use standard truck parameters for paved applications
            if (shouldMapField(targetGeoGridData.truck.wheelRadius, preserveUserValues)) {
                targetGeoGridData.truck.wheelRadius = 0.15; // Standard truck wheel radius
            }
            if (shouldMapField(targetGeoGridData.truck.tirePressure, preserveUserValues)) {
                targetGeoGridData.truck.tirePressure = 600; // Standard tire pressure in kPa
            }
            if (shouldMapField(targetGeoGridData.truck.thickness, preserveUserValues)) {
                targetGeoGridData.truck.thickness = 0.3; // Standard thickness parameter
            }
        }

        // Map surface layer thickness from calculation results
        // Note: The output structure comes from AASHTO calculations, not directly available in IDynamicProjectOutputData
        // For now, we'll use project form data and later enhance this with calculation results
        if (pavedSurface?.minThickness !== undefined) {
            if (!targetGeoGridData.surfaceLayer) {
                targetGeoGridData.surfaceLayer = {}; // Initialize geoGrid.surfaceLayer if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.surfaceLayer.thickness, preserveUserValues)) {
                targetGeoGridData.surfaceLayer.thickness = pavedSurface.minThickness;
            }
        }

        // Map base course thickness from project form data to geogridBaseCourse
        if (pavedBaseCourse?.minThickness !== undefined) {
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {}; // Initialize geoGrid.geogridBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                targetGeoGridData.geogridBaseCourse.thickness = pavedBaseCourse.minThickness;
            }
        }

        // Map subbase course thickness from project form data to geogridSubBaseCourse
        if (pavedSubbaseCourse?.minThickness !== undefined) {
            if (!targetGeoGridData.geogridSubBaseCourse) {
                targetGeoGridData.geogridSubBaseCourse = {}; // Initialize geoGrid.geogridSubBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                targetGeoGridData.geogridSubBaseCourse.thickness = pavedSubbaseCourse.minThickness;
            }
        }

        if (pavedSubgrade?.cbrSubgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subgrade.cbrSubgrade, preserveUserValues)) {
                targetGeoGridData.subgrade.cbrSubgrade = pavedSubgrade.cbrSubgrade;
            }
        }

        if (pavedSubgrade?.ev2Subgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subgrade.deformationModulus, preserveUserValues)) {
                targetGeoGridData.subgrade.deformationModulus = pavedSubgrade.ev2Subgrade;
            }
        }

        // Map base course properties from material selection
        // NOTE: Uses pavedBaseCourse (flat-first with legacy fallback) not pavedData?.baseCourse
        if (pavedBaseCourse?.cbrBaseCourse !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map base CBR for calculating equivalent friction angle and other properties
            // This is an approximation - you might want to use more sophisticated conversion
            const approximateFrictionAngle = Math.min(45, Math.max(25, 25 + Math.sqrt(pavedBaseCourse.cbrBaseCourse) * 2));
            if (shouldMapField(targetGeoGridData.baseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.frictionAngle = approximateFrictionAngle;
            }
            // Set reasonable defaults for other properties
            if (shouldMapField(targetGeoGridData.baseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.baseCourse.unitWeight = 19; // Standard unit weight for base course
            }
            if (shouldMapField(targetGeoGridData.baseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.baseCourse.cohesion = 0; // Typically cohesionless for granular base
            }
            if (shouldMapField(targetGeoGridData.baseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.loadSpreadingAngle = 50; // Standard load spreading angle
            }
        }

        // Map subbase course properties from material selection
        // NOTE: Uses pavedSubbaseCourse (flat-first with legacy fallback) not pavedData?.subbaseCourse
        if (pavedSubbaseCourse?.cbrSubbaseCourse !== undefined) {
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            // Map subbase CBR for calculating equivalent friction angle and other properties
            const approximateFrictionAngle = Math.min(40, Math.max(20, 20 + Math.sqrt(pavedSubbaseCourse.cbrSubbaseCourse) * 1.5));
            if (shouldMapField(targetGeoGridData.subBaseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.frictionAngle = approximateFrictionAngle;
            }
            // Set reasonable defaults for other properties
            if (shouldMapField(targetGeoGridData.subBaseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.unitWeight = 18; // Standard unit weight for subbase course
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.cohesion = 0; // Typically cohesionless for granular subbase
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.loadSpreadingAngle = 45; // Standard load spreading angle
            }
        }

        // Set default safety factor if not already set
        if (!targetGeoGridData.subgrade) {
            targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
        }
        if (shouldMapField(targetGeoGridData.subgrade.safetyFactor, preserveUserValues)) {
            targetGeoGridData.subgrade.safetyFactor = 3; // Standard safety factor for paved applications
        }

        // Enhance mapping with calculated AASHTO results if available
        if (calculatedDesignThickness) {
            const enhancedData = enhancePavedGeogridMappingWithCalculatedResults(targetGeoGridData, calculatedDesignThickness, preserveUserValues, aashtoResults);
            // Apply enhancements by updating the target data
            Object.assign(targetGeoGridData, enhancedData);
        } else if (aashtoResults) {
            // Fallback to basic AASHTO results (legacy behavior)
            if (aashtoResults.detailedCalculations?.softwareDesign) {
                const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;

                // Apply basic AASHTO thickness values
                if (softwareDesign.surface?.stabilised !== undefined) {
                    if (!targetGeoGridData.surfaceLayer) {
                        targetGeoGridData.surfaceLayer = {};
                    }
                    if (shouldMapField(targetGeoGridData.surfaceLayer.thickness, preserveUserValues)) {
                        targetGeoGridData.surfaceLayer.thickness = softwareDesign.surface.stabilised;
                    }
                }

                if (softwareDesign.base?.stabilised !== undefined) {
                    if (!targetGeoGridData.geogridBaseCourse) {
                        targetGeoGridData.geogridBaseCourse = {};
                    }
                    if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                        targetGeoGridData.geogridBaseCourse.thickness = softwareDesign.base.stabilised;
                    }
                }

                if (softwareDesign.subbase?.stabilised !== undefined) {
                    if (!targetGeoGridData.geogridSubBaseCourse) {
                        targetGeoGridData.geogridSubBaseCourse = {};
                    }
                    if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                        targetGeoGridData.geogridSubBaseCourse.thickness = softwareDesign.subbase.stabilised;
                    }
                }
            }
        } else {
        }
    }
    // --- End of "paved" mapping ---
    else if (projectFullData.type === EProjectType.railways) {
        const railwayData = projectFullData; // IRailwayData type

        // --- Load Data Mapping ---
        // Railway data can be structured in two ways:
        // 1. As IRailwayData interface (loadData direct property)
        // 2. As legacy format with default.loadData nested structure
        const loadData = railwayData?.loadData || (railwayData as any)?.default?.loadData;
        const trackGeometry = railwayData?.trackGeometry || (railwayData as any)?.default?.trackGeometry;
        const soilProperties = railwayData?.soilProperties || (railwayData as any)?.default?.soilProperties;

        if (loadData?.designAxleLoad !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            // Map axle load to wheel load (divide by 2 for dual wheels)
            if (shouldMapField(targetGeoGridData.load.wheelLoad, preserveUserValues)) {
                targetGeoGridData.load.wheelLoad = loadData.designAxleLoad / 2;
            }
        }

        // Map wheel diameter for truck configuration
        if (loadData?.wheelDiameter !== undefined) {
            if (!targetGeoGridData.truck) {
                targetGeoGridData.truck = {}; // Initialize geoGrid.truck if it doesn't exist
            }
            // Map wheel diameter to wheel radius
            if (shouldMapField(targetGeoGridData.truck.wheelRadius, preserveUserValues)) {
                targetGeoGridData.truck.wheelRadius = loadData.wheelDiameter / 2;
            }
        }

        // Map train speed for dynamic considerations (using truck configuration)
        if (loadData?.trainSpeed !== undefined) {
            if (!targetGeoGridData.truck) {
                targetGeoGridData.truck = {}; // Initialize geoGrid.truck if it doesn't exist
            }
            // Store train speed in thickness field for railway applications (as dynamic factor reference)
            if (shouldMapField(targetGeoGridData.truck.thickness, preserveUserValues)) {
                targetGeoGridData.truck.thickness = loadData.trainSpeed / 100; // Normalize speed as thickness factor
            }
        }

        // Map rail width for load distribution
        if (trackGeometry?.railWidth !== undefined) {
            if (!targetGeoGridData.load) {
                targetGeoGridData.load = {}; // Initialize geoGrid.load if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.load.deckHalfWidth, preserveUserValues)) {
                targetGeoGridData.load.deckHalfWidth = trackGeometry.railWidth / 2;
            }
        }

        // --- Ballast → Base Course Mapping ---
        if (soilProperties?.cbrOfBallast !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Convert CBR to friction angle using railway-specific correlation
            // Railway ballast: φ = min(50, max(35, 35 + sqrt(CBR) * 3))
            const railwayBallastFrictionAngle = Math.min(50, Math.max(35, 35 + Math.sqrt(soilProperties.cbrOfBallast) * 3));
            if (shouldMapField(targetGeoGridData.baseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.frictionAngle = railwayBallastFrictionAngle;
            }
            // Set standard ballast unit weight (21 kN/m³)
            if (shouldMapField(targetGeoGridData.baseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.baseCourse.unitWeight = 21; // Standard unit weight for railway ballast
            }
            // Set cohesion = 0 (typical for ballast)
            if (shouldMapField(targetGeoGridData.baseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.baseCourse.cohesion = 0; // Cohesionless ballast
            }
            // Set load spreading angle from track geometry or default
            const loadSpreadingAngle = trackGeometry?.loadSpreadingAngle || 55; // Default for railway ballast
            if (shouldMapField(targetGeoGridData.baseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.loadSpreadingAngle = loadSpreadingAngle;
            }
        }

        // Note: ballastType and poissonRatio are not available in baseCourse interface
        // These values are stored for reference but not mapped to geogrid structure

        // --- Subballast → Sub Base Course Mapping ---
        if (soilProperties?.cbrOfSubballast !== undefined) {
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            // Convert CBR to friction angle using railway-specific correlation for subballast
            // Railway subballast: φ = min(45, max(30, 30 + sqrt(CBR) * 2.5))
            const railwaySubballastFrictionAngle = Math.min(45, Math.max(30, 30 + Math.sqrt(soilProperties.cbrOfSubballast) * 2.5));
            if (shouldMapField(targetGeoGridData.subBaseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.frictionAngle = railwaySubballastFrictionAngle;
            }
            // Set standard subballast unit weight (20 kN/m³)
            if (shouldMapField(targetGeoGridData.subBaseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.unitWeight = 20; // Standard unit weight for railway subballast
            }
            // Set low cohesion for subballast (5-10 kPa typical)
            if (shouldMapField(targetGeoGridData.subBaseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.cohesion = 7.5; // Typical cohesion for subballast
            }
            // Set load spreading angle for subballast
            const subballastLoadSpreadingAngle = trackGeometry?.loadSpreadingAngle || 45; // Default for railway subballast
            if (shouldMapField(targetGeoGridData.subBaseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.loadSpreadingAngle = subballastLoadSpreadingAngle;
            }
        }

        // Note: subballast Poisson ratio stored for reference

        // --- Subgrade Mapping ---
        if (soilProperties?.cbrOfSubgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            // Set input method to 'cbr' to show the CBR field in the form
            if (shouldMapField(targetGeoGridData.subgrade.subgradeInputMethod, preserveUserValues)) {
                targetGeoGridData.subgrade.subgradeInputMethod = 'cbr';
            }
            // Map CBR of subgrade directly
            if (shouldMapField(targetGeoGridData.subgrade.cbrSubgrade, preserveUserValues)) {
                targetGeoGridData.subgrade.cbrSubgrade = soilProperties.cbrOfSubgrade;
            }
            // Calculate deformation modulus from CBR using railway-specific correlation
            // Railway subgrade: Ev = CBR * 10 + 50 (MPa) for railway applications
            const railwaySubgradeEv = soilProperties.cbrOfSubgrade * 10 + 50;
            if (shouldMapField(targetGeoGridData.subgrade.deformationModulus, preserveUserValues)) {
                targetGeoGridData.subgrade.deformationModulus = railwaySubgradeEv;
            }
            // Excel MacRailway GeoGrid C45 FS default 3
            if (shouldMapField(targetGeoGridData.subgrade.safetyFactor, preserveUserValues)) {
                targetGeoGridData.subgrade.safetyFactor = GEOGRID_CONFIG.DEFAULT_SAFETY_FACTOR;
            }
        }

        // Note: subgrade soil type and Poisson ratio stored for reference

        // --- Map Existing Geogrid Data from Railway Form ---
        // If the Railway project already has geogrid data configured in the form,
        // preserve the existing geogrid selections in both base and subbase layers
        if (railwayData?.geoGrid) {
            const existingGeoGridData = railwayData.geoGrid;

            // Map existing geogrid base course layer data
            if (existingGeoGridData.geogridBaseCourse) {
                if (!targetGeoGridData.geogridBaseCourse) {
                    targetGeoGridData.geogridBaseCourse = {};
                }
                // Preserve existing geogrid selections and properties
                if (existingGeoGridData.geogridBaseCourse.geogrids && !shouldMapField(targetGeoGridData.geogridBaseCourse.geogrids, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.geogrids = existingGeoGridData.geogridBaseCourse.geogrids;
                }
                // Preserve thickness if not overridden by calculations
                if (existingGeoGridData.geogridBaseCourse.thickness && !shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.thickness = existingGeoGridData.geogridBaseCourse.thickness;
                }
            }

            // Map existing geogrid subbase course layer data
            if (existingGeoGridData.geogridSubBaseCourse) {
                if (!targetGeoGridData.geogridSubBaseCourse) {
                    targetGeoGridData.geogridSubBaseCourse = {};
                }
                // Preserve existing geogrid selections and properties
                if (existingGeoGridData.geogridSubBaseCourse.geogrids && !shouldMapField(targetGeoGridData.geogridSubBaseCourse.geogrids, preserveUserValues)) {
                    targetGeoGridData.geogridSubBaseCourse.geogrids = existingGeoGridData.geogridSubBaseCourse.geogrids;
                }
                // Preserve thickness if not overridden by calculations
                if (existingGeoGridData.geogridSubBaseCourse.thickness && !shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                    targetGeoGridData.geogridSubBaseCourse.thickness = existingGeoGridData.geogridSubBaseCourse.thickness;
                }
            }
        }

        // --- Integration with Calculated Results from RailwayCalculationService ---
        // Check if calculated railway results are available
        const railwayOutputData = outputData as IDynamicProjectOutputData & { railwayCalculation?: IRailwayCalculationResult };

        // Backend response has flat structure with designSummary as array, not nested railwayCalculation object
        // Check if we have backend response format (designSummary array)
        const hasBackendFormat = (outputData as any)?.designSummary && Array.isArray((outputData as any).designSummary);

        // Handle backend format (designSummary as array from API)
        if (hasBackendFormat) {
            const backendData = outputData as any;
            const designSummary = backendData.designSummary as Array<any>;

            // From Results.tsx lines 656-670:
            // Index 2 = Ballast thickness with subballast (h'bs)
            // Index 3 = Subballast thickness (hsbs)
            if (designSummary.length > 3) {
                const ballastWithSubballastRow = designSummary[2]; // h'bs
                const subballastRow = designSummary[3]; // hsbs

                // Map ballast thickness (h'bs - stabilized value)
                if (ballastWithSubballastRow?.stabilizedValue !== undefined && ballastWithSubballastRow.stabilizedValue !== null) {
                    if (!targetGeoGridData.geogridBaseCourse) {
                        targetGeoGridData.geogridBaseCourse = {};
                    }
                    if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                        targetGeoGridData.geogridBaseCourse.thickness = ballastWithSubballastRow.stabilizedValue;
                    }
                }

                // Map subballast thickness (hsbs - stabilized value)
                if (subballastRow?.stabilizedValue !== undefined && subballastRow.stabilizedValue !== null) {
                    if (!targetGeoGridData.geogridSubBaseCourse) {
                        targetGeoGridData.geogridSubBaseCourse = {};
                    }
                    if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                        targetGeoGridData.geogridSubBaseCourse.thickness = subballastRow.stabilizedValue;
                    }
                }
            }

            // Map calculated geometry values from backend (L, bL, uniform pressure)
            // These are now calculated correctly (previously showed N/A)
            if (backendData.calculatedData?.rows) {
                const rows = backendData.calculatedData.rows as Array<any>;

                // Find L (Equivalent length of loaded area on top of ballast)
                const equivalentLengthRow = rows.find((row: any) => row.symbol === 'L' || row.description?.includes('Equivalent length'));
                if (equivalentLengthRow?.value !== undefined && equivalentLengthRow.value !== null) {
                    if (!targetGeoGridData.load) {
                        targetGeoGridData.load = {};
                    }
                    // Map L to Deck Half Length
                    if (shouldMapField(targetGeoGridData.load.deckHalfLength, preserveUserValues)) {
                        targetGeoGridData.load.deckHalfLength = equivalentLengthRow.value;
                    }
                }

                // Find bL (Width of loaded area on top of ballast)
                const widthLoadedAreaRow = rows.find((row: any) => row.symbol === 'bL' || row.description?.includes('Width of loaded area'));
                if (widthLoadedAreaRow?.value !== undefined && widthLoadedAreaRow.value !== null) {
                    if (!targetGeoGridData.load) {
                        targetGeoGridData.load = {};
                    }
                    // Map bL to Deck Half Width
                    if (shouldMapField(targetGeoGridData.load.deckHalfWidth, preserveUserValues)) {
                        targetGeoGridData.load.deckHalfWidth = widthLoadedAreaRow.value;
                    }
                }
            }

            // Map uniform pressure on ballast (σsb) from trainAnalysis
            if (backendData.trainAnalysis && Array.isArray(backendData.trainAnalysis)) {
                // Find the row with uniform pressure on ballast (σsb)
                const uniformPressureRow = backendData.trainAnalysis.find(
                    (row: any) => row.description?.includes('Uniform pressure on ballast') || row.cargoSymbol === 'σsb' || row.passengerSymbol === 'σsb'
                );

                if (uniformPressureRow) {
                    // Use cargo value (stabilized) as it's typically the design case
                    const uniformPressureValue = uniformPressureRow.cargoValue;
                    if (uniformPressureValue !== undefined && uniformPressureValue !== null) {
                        if (!targetGeoGridData.load) {
                            targetGeoGridData.load = {};
                        }
                        // Map uniform pressure (kPa) to uniform load
                        if (shouldMapField(targetGeoGridData.load.uniformLoad, preserveUserValues)) {
                            targetGeoGridData.load.uniformLoad = uniformPressureValue;
                        }
                    }
                }
            }
        }
        // Handle local calculation format (railwayCalculation object from RailwayCalculationOrchestrator)
        else if (railwayOutputData?.railwayCalculation) {
            const calculatedData = railwayOutputData.railwayCalculation;

            // Map calculated ballast thickness from design summary stabilized results
            // Use h'bs (ballastThicknessWithSubballastBelow) which is the stabilized ballast thickness WITH subballast consideration
            // NOT hbs (stabilizedCalculations.ballastThickness) which is the goal-seek result without subballast
            if (calculatedData.designSummary?.stabilized?.ballastThicknessWithSubballastBelow !== undefined) {
                if (!targetGeoGridData.geogridBaseCourse) {
                    targetGeoGridData.geogridBaseCourse = {}; // Initialize geoGrid.geogridBaseCourse if it doesn't exist
                }
                if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.thickness = calculatedData.designSummary.stabilized.ballastThicknessWithSubballastBelow;
                }
            }

            // Map calculated subballast thickness from design summary stabilized results
            // Use hsbs (subballastThickness) from design summary stabilized section
            if (calculatedData.designSummary?.stabilized?.subballastThickness !== undefined) {
                if (!targetGeoGridData.geogridSubBaseCourse) {
                    targetGeoGridData.geogridSubBaseCourse = {}; // Initialize geoGrid.geogridSubBaseCourse if it doesn't exist
                }
                if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                    targetGeoGridData.geogridSubBaseCourse.thickness = calculatedData.designSummary.stabilized.subballastThickness;
                }
            }

            // Alternative: use unstabilized calculations if stabilized not available
            // Use unstabilized ballast thickness with subballast (h'bu) as fallback
            if (!calculatedData.designSummary?.stabilized?.ballastThicknessWithSubballastBelow && calculatedData.designSummary?.unstabilized?.ballastThicknessWithSubballastBelow !== undefined) {
                if (!targetGeoGridData.geogridBaseCourse) {
                    targetGeoGridData.geogridBaseCourse = {}; // Initialize geoGrid.geogridBaseCourse if it doesn't exist
                }
                if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.thickness = calculatedData.designSummary.unstabilized.ballastThicknessWithSubballastBelow;
                }
            }

            // Alternative: use unstabilized subballast if stabilized not available
            if (!calculatedData.designSummary?.stabilized?.subballastThickness && calculatedData.designSummary?.unstabilized?.subballastThickness !== undefined) {
                if (!targetGeoGridData.geogridSubBaseCourse) {
                    targetGeoGridData.geogridSubBaseCourse = {}; // Initialize geoGrid.geogridSubBaseCourse if it doesn't exist
                }
                if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                    targetGeoGridData.geogridSubBaseCourse.thickness = calculatedData.designSummary.unstabilized.subballastThickness;
                }
            }
        }

        // Initialize type object for railway projects
        // Railway uses 'Railway' geogridType (equivalent to 'Rectangular' for calculations)
        if (!targetGeoGridData.type) {
            targetGeoGridData.type = { geogridType: 'Railway' };
        }

        // --- Map selected geogrid product from railway project to geogrid form ---
        // Extract from multiple possible locations for backward compatibility
        const railwayDataWithGeogrid = railwayData as IRailwayData & IRailwayDataWithGeogrid & { default?: { geoGrid?: { selectedGeogrid?: string } } };

        // Try to extract selectedGeogrid from multiple possible locations
        // FLAT-FIRST: Redux stores flat structure after transformProjectFromBackend()
        const selectedGeogrid =
            railwayDataWithGeogrid?.geoGrid?.selectedGeogrid || // PRIMARY: FLAT structure (root level)
            railwayDataWithGeogrid?.selectedGeogrid || // FLAT: direct property
            railwayDataWithGeogrid?.default?.geoGrid?.selectedGeogrid || // FALLBACK: legacy nested under default
            railwayDataWithGeogrid?.selected_geogrid_product; // FALLBACK: legacy snake_case

        if (selectedGeogrid && selectedGeogrid.trim() !== '' && selectedGeogrid !== 'none_selected') {
            // Split product name to extract type
            // Example: "MACGRID EG 20S" → type: "MacGrid EG", product: "MACGRID EG 20S"
            const productParts = selectedGeogrid.trim().split(/\s+/);
            let geogridType = selectedGeogrid; // Default: use full name as type

            if (productParts.length > 1) {
                // Extract type by taking all parts except the last one
                const extractedType = productParts.slice(0, -1).join(' ');

                // Map UPPERCASE type names to Mixed case format that matches dropdown options
                // Backend returns UPPERCASE (e.g., "MACGRID EG 20S"), but dropdown options use Mixed case (e.g., "MacGrid EG")
                const typeNameMapping: { [key: string]: string } = {
                    'MACGRID EG': 'MacGrid EG',
                    'MACGRID EG T': 'MacGrid EG T',
                    'MACGRID WG': 'MacGrid WG',
                    'MACGRID BR': 'MacGrid BR',
                    FORTRAC: 'Fortrac',
                    'FORTRAC T': 'Fortrac T',
                };

                geogridType = typeNameMapping[extractedType.toUpperCase()] || extractedType;
            }

            // Map to BASE layer (geogridBaseCourse)
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {};
            }
            if (!targetGeoGridData.geogridBaseCourse.geogrids) {
                targetGeoGridData.geogridBaseCourse.geogrids = [];
            }

            if (targetGeoGridData.geogridBaseCourse.geogrids.length === 0) {
                // Create new geogrid item for base layer with all required fields
                targetGeoGridData.geogridBaseCourse.geogrids.push({
                    type: geogridType,
                    selectedGeogrid: selectedGeogrid,
                    position: 0.1, // Default position for railway geogrid in base layer
                    designStrain: 2, // Default design strain 2% (matches Paved default)
                });
            } else {
                // Update existing first geogrid in base layer
                if (shouldMapField(targetGeoGridData.geogridBaseCourse.geogrids[0].type, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.geogrids[0].type = geogridType;
                }
                if (shouldMapField(targetGeoGridData.geogridBaseCourse.geogrids[0].selectedGeogrid, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.geogrids[0].selectedGeogrid = selectedGeogrid;
                }
            }

            // Map to SUBBASE layer (geogridSubBaseCourse) - same geogrid as base
            if (!targetGeoGridData.geogridSubBaseCourse) {
                targetGeoGridData.geogridSubBaseCourse = {};
            }
            if (!targetGeoGridData.geogridSubBaseCourse.geogrids) {
                targetGeoGridData.geogridSubBaseCourse.geogrids = [];
            }

            if (targetGeoGridData.geogridSubBaseCourse.geogrids.length === 0) {
                // Create new geogrid item for subbase layer with all required fields
                targetGeoGridData.geogridSubBaseCourse.geogrids.push({
                    type: geogridType,
                    selectedGeogrid: selectedGeogrid,
                    position: 0.1, // Default position for railway geogrid in subbase layer
                    designStrain: 2, // Default design strain 2% (matches Paved default)
                });
            } else {
                // Update existing first geogrid in subbase layer
                if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.geogrids[0].type, preserveUserValues)) {
                    targetGeoGridData.geogridSubBaseCourse.geogrids[0].type = geogridType;
                }
                if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.geogrids[0].selectedGeogrid, preserveUserValues)) {
                    targetGeoGridData.geogridSubBaseCourse.geogrids[0].selectedGeogrid = selectedGeogrid;
                }
            }
        }
    }
    // --- End of "railways" mapping ---
    else if (projectFullData.type === EProjectType.airport) {
        const airportData = projectFullData; // IAirportData type

        // Initialize type object for airport projects to avoid warning in ensureGeoGridType
        if (!targetGeoGridData.type) {
            targetGeoGridData.type = { geogridType: 'Circular' };
        }

        // Airport projects use identical structure to paved projects with asphalt method
        // Map wheel load information from traffic load section
        if (airportData?.trafficLoad?.esalFinalValue !== undefined) {
            if (!targetGeoGridData.truck) {
                targetGeoGridData.truck = {}; // Initialize geoGrid.truck if it doesn't exist
            }
            // Use standard aircraft parameters for airport applications
            if (shouldMapField(targetGeoGridData.truck.wheelRadius, preserveUserValues)) {
                targetGeoGridData.truck.wheelRadius = 0.25; // Standard aircraft wheel radius (larger than truck)
            }
            if (shouldMapField(targetGeoGridData.truck.tirePressure, preserveUserValues)) {
                targetGeoGridData.truck.tirePressure = 1200; // Standard aircraft tire pressure in kPa (higher than truck)
            }
            if (shouldMapField(targetGeoGridData.truck.thickness, preserveUserValues)) {
                targetGeoGridData.truck.thickness = 0.4; // Standard thickness parameter for aircraft
            }
        }

        // Map surface layer thickness from project form data - THIS IS THE KEY MAPPING FOR AIRPORT
        if (airportData?.surface?.minThickness !== undefined) {
            if (!targetGeoGridData.surfaceLayer) {
                targetGeoGridData.surfaceLayer = {}; // Initialize geoGrid.surfaceLayer if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.surfaceLayer.thickness, preserveUserValues)) {
                targetGeoGridData.surfaceLayer.thickness = airportData.surface.minThickness / 1000; // Convert from mm to m
            }
        }

        // Fallback: check legacy asphalt structure for surface thickness
        if (!targetGeoGridData.surfaceLayer?.thickness && airportData?.asphalt?.surface?.minThickness !== undefined) {
            if (!targetGeoGridData.surfaceLayer) {
                targetGeoGridData.surfaceLayer = {}; // Initialize geoGrid.surfaceLayer if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.surfaceLayer.thickness, preserveUserValues)) {
                targetGeoGridData.surfaceLayer.thickness = airportData.asphalt.surface.minThickness / 1000; // Convert from mm to m
            }
        }

        // Map base course thickness from project form data to geogridBaseCourse
        if (airportData?.baseCourse?.minThickness !== undefined) {
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {}; // Initialize geoGrid.geogridBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                targetGeoGridData.geogridBaseCourse.thickness = airportData.baseCourse.minThickness / 1000; // Convert from mm to m
            }
        }

        // Fallback: check legacy asphalt structure for base thickness
        if (!targetGeoGridData.geogridBaseCourse?.thickness && airportData?.asphalt?.baseCourse?.minThickness !== undefined) {
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {}; // Initialize geoGrid.geogridBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                targetGeoGridData.geogridBaseCourse.thickness = airportData.asphalt.baseCourse.minThickness / 1000; // Convert from mm to m
            }
        }

        // Map subbase course thickness from project form data to geogridSubBaseCourse
        if (airportData?.subbaseCourse?.minThickness !== undefined) {
            if (!targetGeoGridData.geogridSubBaseCourse) {
                targetGeoGridData.geogridSubBaseCourse = {}; // Initialize geoGrid.geogridSubBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                targetGeoGridData.geogridSubBaseCourse.thickness = airportData.subbaseCourse.minThickness / 1000; // Convert from mm to m
            }
        }

        // Fallback: check legacy asphalt structure for subbase thickness
        if (!targetGeoGridData.geogridSubBaseCourse?.thickness && airportData?.asphalt?.subbaseCourse?.minThickness !== undefined) {
            if (!targetGeoGridData.geogridSubBaseCourse) {
                targetGeoGridData.geogridSubBaseCourse = {}; // Initialize geoGrid.geogridSubBaseCourse if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                targetGeoGridData.geogridSubBaseCourse.thickness = airportData.asphalt.subbaseCourse.minThickness / 1000; // Convert from mm to m
            }
        }

        // Map subgrade CBR from road info section
        if (airportData?.subgrade?.cbrSubgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subgrade.cbrSubgrade, preserveUserValues)) {
                targetGeoGridData.subgrade.cbrSubgrade = airportData.subgrade.cbrSubgrade;
            }
        }

        // Fallback: check legacy asphalt structure for subgrade CBR
        if (!targetGeoGridData.subgrade?.cbrSubgrade && airportData?.asphalt?.subgrade?.cbrSubgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subgrade.cbrSubgrade, preserveUserValues)) {
                targetGeoGridData.subgrade.cbrSubgrade = airportData.asphalt.subgrade.cbrSubgrade;
            }
        }

        // Map subgrade Ev2 from road info section
        if (airportData?.subgrade?.ev2Subgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subgrade.deformationModulus, preserveUserValues)) {
                targetGeoGridData.subgrade.deformationModulus = airportData.subgrade.ev2Subgrade;
            }
        }

        // Fallback: check legacy asphalt structure for subgrade Ev2
        if (!targetGeoGridData.subgrade?.deformationModulus && airportData?.asphalt?.subgrade?.ev2Subgrade !== undefined) {
            if (!targetGeoGridData.subgrade) {
                targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
            }
            if (shouldMapField(targetGeoGridData.subgrade.deformationModulus, preserveUserValues)) {
                targetGeoGridData.subgrade.deformationModulus = airportData.asphalt.subgrade.ev2Subgrade;
            }
        }

        // Map base course properties from material selection (similar to paved but with airport-specific parameters)
        if (airportData?.baseCourse?.cbrBaseCourse !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            // Map base CBR for calculating equivalent friction angle - airport specific correlation
            const approximateFrictionAngle = Math.min(50, Math.max(30, 30 + Math.sqrt(airportData.baseCourse.cbrBaseCourse) * 2.5));
            if (shouldMapField(targetGeoGridData.baseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.frictionAngle = approximateFrictionAngle;
            }
            // Set reasonable defaults for airport base course properties
            if (shouldMapField(targetGeoGridData.baseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.baseCourse.unitWeight = 20; // Higher unit weight for airport base course
            }
            if (shouldMapField(targetGeoGridData.baseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.baseCourse.cohesion = 0; // Typically cohesionless for granular base
            }
            if (shouldMapField(targetGeoGridData.baseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.loadSpreadingAngle = 55; // Higher load spreading angle for airport applications
            }
        }

        // Fallback: check legacy asphalt structure for base course CBR
        if (!targetGeoGridData.baseCourse?.frictionAngle && airportData?.asphalt?.baseCourse?.cbrBaseCourse !== undefined) {
            if (!targetGeoGridData.baseCourse) {
                targetGeoGridData.baseCourse = {}; // Initialize geoGrid.baseCourse if it doesn't exist
            }
            const approximateFrictionAngle = Math.min(50, Math.max(30, 30 + Math.sqrt(airportData.asphalt.baseCourse.cbrBaseCourse) * 2.5));
            if (shouldMapField(targetGeoGridData.baseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.frictionAngle = approximateFrictionAngle;
            }
            if (shouldMapField(targetGeoGridData.baseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.baseCourse.unitWeight = 20; // Higher unit weight for airport base course
            }
            if (shouldMapField(targetGeoGridData.baseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.baseCourse.cohesion = 0; // Typically cohesionless for granular base
            }
            if (shouldMapField(targetGeoGridData.baseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.baseCourse.loadSpreadingAngle = 55; // Higher load spreading angle for airport applications
            }
        }

        // Map subbase course properties from material selection (similar to paved but with airport-specific parameters)
        if (airportData?.subbaseCourse?.cbrSubbaseCourse !== undefined) {
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            // Map subbase CBR for calculating equivalent friction angle - airport specific correlation
            const approximateFrictionAngle = Math.min(45, Math.max(25, 25 + Math.sqrt(airportData.subbaseCourse.cbrSubbaseCourse) * 2));
            if (shouldMapField(targetGeoGridData.subBaseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.frictionAngle = approximateFrictionAngle;
            }
            // Set reasonable defaults for airport subbase course properties
            if (shouldMapField(targetGeoGridData.subBaseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.unitWeight = 19; // Standard unit weight for airport subbase course
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.cohesion = 0; // Typically cohesionless for granular subbase
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.loadSpreadingAngle = 50; // Higher load spreading angle for airport applications
            }
        }

        // Fallback: check legacy asphalt structure for subbase course CBR
        if (!targetGeoGridData.subBaseCourse?.frictionAngle && airportData?.asphalt?.subbaseCourse?.cbrSubbaseCourse !== undefined) {
            if (!targetGeoGridData.subBaseCourse) {
                targetGeoGridData.subBaseCourse = {}; // Initialize geoGrid.subBaseCourse if it doesn't exist
            }
            const approximateFrictionAngle = Math.min(45, Math.max(25, 25 + Math.sqrt(airportData.asphalt.subbaseCourse.cbrSubbaseCourse) * 2));
            if (shouldMapField(targetGeoGridData.subBaseCourse.frictionAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.frictionAngle = approximateFrictionAngle;
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.unitWeight, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.unitWeight = 19; // Standard unit weight for airport subbase course
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.cohesion, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.cohesion = 0; // Typically cohesionless for granular subbase
            }
            if (shouldMapField(targetGeoGridData.subBaseCourse.loadSpreadingAngle, preserveUserValues)) {
                targetGeoGridData.subBaseCourse.loadSpreadingAngle = 50; // Higher load spreading angle for airport applications
            }
        }

        // Excel GeoGrid Circ C49 FS default 3
        if (!targetGeoGridData.subgrade) {
            targetGeoGridData.subgrade = {}; // Initialize geoGrid.subgrade if it doesn't exist
        }
        if (shouldMapField(targetGeoGridData.subgrade.safetyFactor, preserveUserValues)) {
            targetGeoGridData.subgrade.safetyFactor = GEOGRID_CONFIG.DEFAULT_SAFETY_FACTOR;
        }

        // Enhance mapping with calculated AASHTO results if available (same as paved since airports use AASHTO method)
        if (calculatedDesignThickness) {
            const enhancedData = enhancePavedGeogridMappingWithCalculatedResults(targetGeoGridData, calculatedDesignThickness, preserveUserValues, aashtoResults);
            // Apply enhancements by updating the target data
            Object.assign(targetGeoGridData, enhancedData);
        } else if (aashtoResults) {
            // Fallback to basic AASHTO results (legacy behavior)
            if (aashtoResults.detailedCalculations?.softwareDesign) {
                const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;

                // Apply basic AASHTO thickness values for airport
                if (softwareDesign.surface?.stabilised !== undefined) {
                    if (!targetGeoGridData.surfaceLayer) {
                        targetGeoGridData.surfaceLayer = {};
                    }
                    if (shouldMapField(targetGeoGridData.surfaceLayer.thickness, preserveUserValues)) {
                        targetGeoGridData.surfaceLayer.thickness = softwareDesign.surface.stabilised;
                    }
                }

                if (softwareDesign.base?.stabilised !== undefined) {
                    if (!targetGeoGridData.geogridBaseCourse) {
                        targetGeoGridData.geogridBaseCourse = {};
                    }
                    if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                        targetGeoGridData.geogridBaseCourse.thickness = softwareDesign.base.stabilised;
                    }
                }

                if (softwareDesign.subbase?.stabilised !== undefined) {
                    if (!targetGeoGridData.geogridSubBaseCourse) {
                        targetGeoGridData.geogridSubBaseCourse = {};
                    }
                    if (shouldMapField(targetGeoGridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
                        targetGeoGridData.geogridSubBaseCourse.thickness = softwareDesign.subbase.stabilised;
                    }
                }
            }
        } else {
            // Log that airport mapping was applied but no AASHTO results available
        }
    }
    // --- End of "airport" mapping ---
    else if (projectFullData.type === EProjectType.unpaved) {
        // Map calculated geogrid stabilised thickness from unpaved results to geogrid form
        // The geogridStabilisedThickness (hgg) from results should be copied to geogridBaseCourse.thickness
        const unpavedOutput = outputData as any;
        if (unpavedOutput?.geogridStabilisedThickness !== undefined) {
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {};
            }
            if (shouldMapField(targetGeoGridData.geogridBaseCourse.thickness, preserveUserValues)) {
                targetGeoGridData.geogridBaseCourse.thickness = unpavedOutput.geogridStabilisedThickness;
            }
        }

        // Map selected geogrid product from unpaved project to geogrid form
        // Extract from giroud or leng methods based on project method
        const unpavedData = projectFullData as IUnpavedData;

        // FLAT structure: method is a property, data is at root
        const method = unpavedData.method || 'giroud';
        const selectedGeogrid = (unpavedData as any)?.geoGrid?.selectedGeogrid;

        if (selectedGeogrid && selectedGeogrid.trim() !== '' && selectedGeogrid !== 'none_selected') {
            // Initialize geogridBaseCourse if needed
            if (!targetGeoGridData.geogridBaseCourse) {
                targetGeoGridData.geogridBaseCourse = {};
            }
            // Initialize geogrids array if needed
            if (!targetGeoGridData.geogridBaseCourse.geogrids) {
                targetGeoGridData.geogridBaseCourse.geogrids = [];
            }

            // Split product name to extract type
            // Example: "MACGRID WG 03S" → type: "MACGRID WG", product: "MACGRID WG 03S"
            // The type is everything except the last space-separated segment
            const productParts = selectedGeogrid.trim().split(/\s+/);
            let extractedType = selectedGeogrid; // Default: use full name as type

            if (productParts.length > 1) {
                // Extract type by taking all parts except the last one
                extractedType = productParts.slice(0, -1).join(' ');
            }

            // Map extracted type (uppercase from product name) to dropdown option value (mixed case)
            const geogridType = mapGeogridTypeToDropdownValue(extractedType);

            // Map to first geogrid in array (or create if doesn't exist)
            if (targetGeoGridData.geogridBaseCourse.geogrids.length === 0) {
                // Create new geogrid item
                targetGeoGridData.geogridBaseCourse.geogrids.push({
                    type: geogridType,
                    selectedGeogrid: selectedGeogrid,
                    // Height is required per layer; 0 is what the Ttot calculation used when this layer had none
                    position: 0,
                });
            } else {
                // Update existing first geogrid
                if (shouldMapField(targetGeoGridData.geogridBaseCourse.geogrids[0].type, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.geogrids[0].type = geogridType;
                }
                if (shouldMapField(targetGeoGridData.geogridBaseCourse.geogrids[0].selectedGeogrid, preserveUserValues)) {
                    targetGeoGridData.geogridBaseCourse.geogrids[0].selectedGeogrid = selectedGeogrid;
                }
            }
        }
    }
    // --- End of "unpaved" mapping ---

    // Return the mapped data without unit conversion
    // Unit conversion will be handled later in the pipeline to avoid double conversion
    return keepSavedGeogridSafetyFactor(projectFullData.geoGrid, targetGeoGridData);
};

// Export aliases for backward compatibility
export const mapProjectDataToGeogridInputs = mapExistingProjectToGeogridForm; // Legacy alias
export const mapProjectSpecificDataToGeoGrid = mapExistingProjectToGeogridForm; // Legacy alias
